// src/components/TicketMessages.tsx
import React, { useEffect, useRef, useState } from "react";
import { MessageModel, TicketModel } from "../../types/index";
import { getUserName } from "../../api/sessions";
import { getSupportSocket } from "../../api/support-socket";
import { listTicketMessages, createTicketMessage } from "../../api/support";
import { useTranslation } from "react-i18next";
import HtmlEditor, { Item, Toolbar as HTMLToolbar } from "devextreme-react/html-editor";
import Button from "devextreme-react/button";
import Splitter, { Item as SplitterItem } from 'devextreme-react/splitter';

type Props = {
  vendorId: string;
  ticketId: string;
  ticketStatus: number | undefined;
  ticket: TicketModel;
};


export const TicketMessages: React.FC<Props> = ({ vendorId, ticketId, ticketStatus, ticket }) => {
  const [messages, setMessages] = useState<MessageModel[]>([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const user = getUserName();
  const endRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { t, i18n } = useTranslation();

  const normalizeMessage = (raw: any): MessageModel | null => {
    if (!raw || typeof raw !== "object") return null;
    const id = raw.id ?? raw.ref ?? raw._id;
    if (!id) return null;
    return {
      ...raw,
      id: String(id),
      sender: raw.sender ?? raw.sender_type ?? "support",
      text: raw.text ?? raw.content ?? raw.body ?? "",
      createdAt: raw.createdAt ?? raw.created_at,
      createdBy: raw.createdBy ?? raw.sender_name ?? raw.created_by,
    };
  };

  useEffect(() => {
    if (!ticketId) return;
    let cancelled = false;
    setMessages([]);
    (async () => {
      try {
        const data = await listTicketMessages(ticketId);
        if (cancelled) return;
        const arr: any[] = Array.isArray(data) ? data : (data?.records ?? data?.items ?? data?.data ?? []);
        const list: MessageModel[] = arr
          .filter((m: any) => !ticketId || !m.ticket_ref || m.ticket_ref === ticketId)
          .map(normalizeMessage)
          .filter(Boolean) as MessageModel[];
        setMessages(list);
      } catch (err) {
        console.warn("[TicketMessages] list failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [vendorId, ticketId]);

  useEffect(() => {
    if (!ticketId) return;
    const socket = getSupportSocket();

    const handler = (msg: any) => {
      console.log("[TicketMessages] event", msg);
      const payload = msg?.payload ?? msg;
      const type = payload?.type ?? payload?.event ?? msg?.event;
      if (type && !String(type).includes("MESSAGE")) return;

      const ref = payload?.ticketRef ?? payload?.ticket_ref ?? payload?.ref ?? payload?.ticket?.ref ?? payload?.ticket?.id;
      if (ref && String(ref) !== String(ticketId)) return;

      const raw = payload?.message ?? payload?.data ?? payload;
      const incoming = normalizeMessage(raw);
      if (!incoming) return;
      setMessages((prev) => (prev.some((x) => x.id === incoming.id) ? prev : [...prev, incoming]));
    };

    socket.on("queue.notification.push", handler);
    socket.on("queue.notification.email", handler);
    socket.on("queue.notification.sms", handler);
    return () => {
      socket.off("queue.notification.push", handler);
      socket.off("queue.notification.email", handler);
      socket.off("queue.notification.sms", handler);
    };
  }, [ticketId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 50);
    return () => clearTimeout(timer);
  }, [messages]);

  const canReply = ticketStatus === 1; // only when support replied

  function isEmptyHtml(value: string) {
    const plain = value
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return plain.length === 0;
  }

  function formatDateTime(ts: any) {
    try {
      if (!ts) return "";
      const d = ts?.toDate ? ts.toDate() : new Date(ts);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleString(i18n.language, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  }

  function getSenderLabel(sender?: string, createdBy?: string) {
    if (sender === "support") return t("supportAgent");
    if (sender === "customer") return createdBy?.trim() ? createdBy : t("customer");
    return createdBy?.trim() ? createdBy : t("customer");
  }

  async function sendMessage() {
    if (sending) return;
    if ((isEmptyHtml(text) && !file)) return;

    setSending(true);
    try {
      await createTicketMessage({
        ticket_ref: ticketId,
        content: text,
        sender_id: String(vendorId ?? ""),
        sender_name: user ?? "",
        sender_type: "customer",
        is_internal: false,
        attachments: [],
      });

      setFile(null);
      setText("");

      try {
        const data = await listTicketMessages(ticketId);
        const arr: any[] = Array.isArray(data) ? data : (data?.records ?? data?.items ?? data?.data ?? []);
        const list: MessageModel[] = arr
          .filter((m: any) => !ticketId || !m.ticket_ref || m.ticket_ref === ticketId)
          .map(normalizeMessage)
          .filter(Boolean) as MessageModel[];
        setMessages(list);
      } catch (e) {
        console.warn("[TicketMessages] refetch failed:", e);
      }
    } catch (err) {
      console.error("[TicketMessages] send failed:", err);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0 rounded-lg border overflow-hidden support-surface support-border">
      <Splitter orientation="vertical" style={{ height: "100%" }}>
        <SplitterItem size="70%" minSize="30%" resizable>
          <div className="h-full overflow-y-auto p-4 support-chat-bg">
        {!ticket.description && (!ticket.attachments || ticket.attachments.length === 0) && messages.length === 0 && (
          <div className="h-full flex items-center justify-center text-sm support-text-muted">
            {t("noMessagesYet")}
          </div>
        )}

        {(!!ticket.description || !!ticket.attachments?.length) && (
          <div className="mb-4">
            <div className="support-msg-row">
              <div className="support-msg-avatar support-msg-avatar-me">
                <i className="dx-icon dx-icon-user" style={{ fontSize: 16 }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold">
                    {getSenderLabel("customer", ticket.createdBy)}
                  </span>
                  <span className="text-[11px] support-text-muted">
                    {formatDateTime(ticket.createdAt)}
                  </span>
                </div>
                <div className="support-msg-body support-msg-body-me">
                  {!!ticket.description && (
                    <div
                      className="text-sm leading-relaxed break-words prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: ticket.description ?? "" }}
                    />
                  )}
                  {!!ticket.attachments?.length && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {ticket.attachments.map((a, idx) => (
                        <a key={a} href={a} target="_blank" rel="noreferrer"
                          className="text-sm underline inline-flex items-center gap-1">
                          <i className="dx-icon dx-icon-attach" style={{ fontSize: 14 }} />
                          {t("file")} {idx + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {messages.map((m) => {
          const isCustomer = m.sender === "customer";
          return (
            <div key={m.id} className="mb-4">
              <div className="support-msg-row">
                <div className={`support-msg-avatar ${isCustomer ? 'support-msg-avatar-me' : 'support-msg-avatar-agent'}`}>
                  <i className={`dx-icon dx-icon-${isCustomer ? 'user' : 'tel'}`} style={{ fontSize: 16 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold ${isCustomer ? '' : 'support-agent-name'}`}>
                      {getSenderLabel(m.sender, m.createdBy)}
                    </span>
                    <span className="text-[11px] support-text-muted">
                      {formatDateTime(m.createdAt)}
                    </span>
                  </div>
                  <div className={`support-msg-body ${isCustomer ? 'support-msg-body-me' : 'support-msg-body-agent'}`}>
                    {m.text && (
                      <div
                        className="text-sm leading-relaxed break-words prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: m.text }}
                      />
                    )}
                    {m.fileUrl && (
                      <div className="mt-2">
                        <a
                          href={m.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm underline inline-flex items-center gap-1"
                        >
                          <i className="dx-icon dx-icon-attach" style={{ fontSize: 14 }} />
                          {t("openFile")}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
        </SplitterItem>

        <SplitterItem minSize="120px" resizable>
      <div className="border-t p-2 flex flex-col gap-2 support-border h-full overflow-y-auto">
        <input
          ref={fileInputRef}
          type="file"
          disabled={!canReply || sending}
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />

        <div className="flex items-center gap-2">
          <Button
            icon="attach"
            text={t("file")}
            stylingMode="outlined"
            disabled={!canReply || sending}
            onClick={() => fileInputRef.current?.click()}
          />

          {file && (
            <div className="min-w-0 flex items-center gap-2 text-sm support-text-muted">
              <span className="truncate max-w-[260px]">{file.name}</span>
              <button
                type="button"
                className="underline"
                disabled={!canReply || sending}
                onClick={() => setFile(null)}
              >
                {t("remove")}
              </button>
            </div>
          )}

          <div className="ml-auto">
            <Button
              icon="send"
              text={sending ? t("sending") : t("send")}
              type="default"
              stylingMode="contained"
              disabled={!canReply || sending}
              onClick={sendMessage}
            />
          </div>
        </div>

        <div className={`rounded-md border overflow-hidden support-border flex-1 ${(!canReply || sending) ? "opacity-60" : ""}`}>
          <HtmlEditor
            height={"100%"}
            value={text}
            onValueChanged={(e) => setText(e.value)}
            readOnly={!canReply || sending}
          >
            <HTMLToolbar>
              <Item name="undo" />
              <Item name="redo" />
              <Item name="separator" />
              <Item name="bold" />
              <Item name="italic" />
              <Item name="strike" />
              <Item name="underline" />
              <Item name="separator" />
              <Item name="alignLeft" />
              <Item name="alignCenter" />
              <Item name="alignRight" />
              <Item name="alignJustify" />
              <Item name="separator" />
              <Item name="orderedList" />
              <Item name="bulletList" />
              <Item name="separator" />
              <Item name="color" />
              <Item name="background" />
              <Item name="separator" />
              <Item name="link" />
              <Item name="image" />
              <Item name="separator" />
              <Item name="clear" />
              <Item name="codeBlock" />
              <Item name="blockquote" />
            </HTMLToolbar>
          </HtmlEditor>
        </div>

        {!canReply && (
          <div className="text-xs support-text-muted">
            {t("waitForSupportReply")}
          </div>
        )}
      </div>
        </SplitterItem>
      </Splitter>
    </div>
  );
};
